import { RRule } from 'rrule'
import { UserMetadata } from './user'

/**
 * The six email notifications of the EMAIL block (cadrage §2.8).
 * `digest` and `meetingStart` already ship today; the other four arrive with
 * E2 to E4 and have no sender yet.
 */
export type NotificationType =
  | 'meetingSummary' // Meeting minutes, sent on closing (E2)
  | 'digest' // Periodic digest (already live)
  | 'proposalOpened' // A proposal is open for voting (E3)
  | 'proposalClosed' // Result of a proposal, to its author (E3)
  | 'roleAssigned' // Someone assigned you a role (E4)
  | 'meetingStart' // A meeting you attend just started (already live)

/**
 * The pivot date used when the instance declares none.
 *
 * 🔑 Both settings files fall back to THIS constant, so a deployment that
 * forgets the variable still has the backend and the webapp agreeing. They
 * used to hold a literal each, and already disagreed outside Yocoop.
 */
export const DEFAULT_NOTIFICATIONS_DEFAULTS_SINCE = '2026-08-12'

/**
 * What an unset preference means.
 *
 * `legacyEnabled` is the only way to disable by default an email that already
 * ships, without taking it away from the people who receive it today: enabled
 * for accounts created before the instance's pivot date, disabled after
 * (EM-D3 + EM-D6).
 */
type NotificationDefault = boolean | 'legacyEnabled'

/**
 * Which section of the settings screen a notification belongs to (EMAIL-10).
 *
 * 🔑 Grouped by SUBJECT, not by when the email leaves: Yohan asked for a
 * "meeting" group and an "async" group after using E5 (13/08/2026), which
 * replaces EM-D26's "as it happens" / "at a fixed time" split.
 *
 * ⚠️ He named two groups; two notifications belong to neither. A role
 * assignment is neither a meeting nor a vote, hence `orgChart`; the digest
 * keeps its own section because it is the only one carrying a schedule.
 *
 * ⚠️ Deliberately NOT conditioned on the organization's async setting, though
 * that was the initial request: `asyncGovernanceEnabled` belongs to an
 * organization while these preferences belong to a person and live outside any
 * organization. Someone in three orgs, one of which has async on, would lose
 * the switch for an email they do receive. Dropped by Yohan on 13/08 once the
 * conflict with EM-D2 was named.
 */
export type NotificationGroup = 'meeting' | 'async' | 'orgChart' | 'scheduled'

/** The groups, in the order the settings screen shows them. */
export const notificationGroups: NotificationGroup[] = [
  'meeting',
  'async',
  'orgChart',
  'scheduled',
]

interface NotificationConfig {
  /** Explicit preference, or undefined when the user never set it. */
  read: (metadata: UserMetadata) => boolean | undefined
  default: NotificationDefault
  /**
   * The metadata field this notification is switched on and off by.
   *
   * 🔑 Exposed so a settings screen can WRITE what `read` READS, without
   * keeping its own list of types and keys beside this one — the second
   * source of truth that always ends up drifting.
   *
   * Absent for `digest` alone: its state lives in its own RRULE, not in a
   * boolean, which is precisely what tells the two groups of the settings
   * screen apart (E5, EM-D26).
   */
  metadataKey?: BooleanPreferenceKey
  /** The settings screen section this notification is shown in. */
  group: NotificationGroup
}

type BooleanPreferenceKey =
  | 'notifMeetingSummary'
  | 'notifProposalOpened'
  | 'notifProposalClosed'
  | 'notifRoleAssigned'
  | 'notifMeetingStart'

const readBoolean =
  (key: BooleanPreferenceKey) =>
  (metadata: UserMetadata): boolean | undefined =>
    metadata[key]

/**
 * The defaults of cadrage §2.8, and the metadata key each notification reads.
 * Callers name a NotificationType and never a metadata field.
 */
export const notificationsConfig: Record<NotificationType, NotificationConfig> =
  {
    meetingSummary: {
      read: readBoolean('notifMeetingSummary'),
      metadataKey: 'notifMeetingSummary',
      group: 'meeting',
      default: true,
    },
    digest: {
      // The digest carries no boolean: its own RRULE holds its "disabled"
      // state, `false` meaning the user switched it off.
      read: (metadata) =>
        metadata.digestRrule === undefined
          ? undefined
          : metadata.digestRrule !== false,
      group: 'scheduled',
      default: true,
    },
    proposalOpened: {
      // Enabled on purpose, against the initial instruction: without this
      // email the vote never starts, and the proposal reaches its deadline
      // in silence, resolved without the voices it was waiting for.
      read: readBoolean('notifProposalOpened'),
      metadataKey: 'notifProposalOpened',
      group: 'async',
      default: true,
    },
    proposalClosed: {
      // Rare, and its author is waiting for the answer.
      read: readBoolean('notifProposalClosed'),
      metadataKey: 'notifProposalClosed',
      group: 'async',
      default: true,
    },
    roleAssigned: {
      // Someone most likely told you in person.
      read: readBoolean('notifRoleAssigned'),
      metadataKey: 'notifRoleAssigned',
      group: 'orgChart',
      default: false,
    },
    meetingStart: {
      // Kept as is for accounts that already receive it (EM-D6).
      read: readBoolean('notifMeetingStart'),
      metadataKey: 'notifMeetingStart',
      group: 'meeting',
      default: 'legacyEnabled',
    },
  }

/**
 * The notifications that are a plain on/off preference.
 *
 * 🔑 The type-level counterpart of `metadataKey`: every entry of
 * `notificationsConfig` carries one except `digest`, whose state lives in its
 * RRULE. Kept right here so the two statements of the same fact cannot drift
 * apart — a caller filtering on `metadataKey` narrows to exactly this type.
 */
export type InstantNotificationType = Exclude<NotificationType, 'digest'>

/**
 * Whether an account predates the instance's pivot date, and therefore keeps
 * the defaults it lives with today.
 *
 * An account created exactly at the pivot date is a new account.
 *
 * 🔑 An unreadable or missing creation date counts as a LEGACY account, on
 * purpose. A plain `<` would answer `false` on `NaN` and treat the account as
 * new, which silently takes away an email the person receives today — the one
 * thing EM-D3 forbids. When in doubt, keep sending.
 */
export const isLegacyAccount = (
  createdAt: string | Date,
  defaultsSince: string | Date
): boolean => {
  const created = new Date(createdAt).getTime()
  if (Number.isNaN(created)) return true
  return created < new Date(defaultsSince).getTime()
}

/**
 * The digest schedule someone gets when they never chose one.
 *
 * Monday alone from the pivot date on (EM-D8); the accounts that live with
 * Monday and Thursday today keep both. Nothing is written anywhere: the
 * default is a function of when the account was created, which is why this
 * needs no migration.
 */
export const getDefaultDigestRRule = (
  dtstart: string,
  defaultsSince: string | Date
) =>
  new RRule({
    dtstart: new Date(dtstart),
    freq: RRule.WEEKLY,
    byweekday: isLegacyAccount(dtstart, defaultsSince)
      ? [RRule.MO, RRule.TH]
      : [RRule.MO],
  })

/**
 * Does this person want this email?
 *
 * The single entry point: no caller reads a preference by hand. An explicit
 * choice always wins; failing that the §2.8 default applies; a `legacyEnabled`
 * default then depends on when the account was created.
 *
 * Pure on purpose — the pivot date is passed in, never read: this module is
 * imported by the backend, which reads dotenv, and by the webapp, which reads
 * no environment at all.
 */
export const wantsNotification = (
  type: NotificationType,
  metadata: UserMetadata | undefined | null,
  createdAt: string | Date,
  defaultsSince: string | Date
): boolean => {
  const config = notificationsConfig[type]

  const explicit = metadata ? config.read(metadata) : undefined
  if (explicit !== undefined) return explicit

  if (config.default === 'legacyEnabled') {
    return isLegacyAccount(createdAt, defaultsSince)
  }
  return config.default
}
